import { useState, useEffect } from "react";
import { Save, Globe, CreditCard, Truck, Mail, Shield, MessageSquare, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SettingCard({ icon: Icon, title, children }) {
  return (<div className="bg-white rounded-2xl p-6 shadow-sm border"><div className="flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-cyan-600" /><h3 className="font-semibold text-slate-800">{title}</h3></div>{children}</div>);
}

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");

  useEffect(() => { axios.get(`${API}/admin/settings`, { withCredentials: true }).then(r => setSettings(r.data)).finally(() => setLoading(false)); }, []);

  const save = async () => {
    try { await axios.put(`${API}/admin/settings`, settings, { withCredentials: true }); toast.success("Settings saved!"); } catch { toast.error("Failed"); }
  };
  const u = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-40 skeleton-loading rounded-2xl" />)}</div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <Button onClick={save} className="rounded-full bg-cyan-600 hover:bg-cyan-700"><Save className="w-4 h-4 mr-1" /> Save All</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white rounded-xl border mb-4 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="general" className="rounded-xl text-xs">General</TabsTrigger>
          <TabsTrigger value="payment" className="rounded-xl text-xs">Payment</TabsTrigger>
          <TabsTrigger value="delivery" className="rounded-xl text-xs">Delivery</TabsTrigger>
          <TabsTrigger value="email" className="rounded-xl text-xs">Email</TabsTrigger>
          <TabsTrigger value="security" className="rounded-xl text-xs">Security</TabsTrigger>
          <TabsTrigger value="tax" className="rounded-xl text-xs">Tax</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <SettingCard icon={Globe} title="General Settings">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Site Name</Label><Input value={settings.site_name || ""} onChange={e => u("site_name", e.target.value)} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Support Email</Label><Input value={settings.support_email || ""} onChange={e => u("support_email", e.target.value)} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Support Phone</Label><Input value={settings.support_phone || ""} onChange={e => u("support_phone", e.target.value)} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Currency</Label><Input value={settings.currency || "INR"} onChange={e => u("currency", e.target.value)} className="rounded-xl mt-1" /></div>
            </div>
          </SettingCard>
        </TabsContent>

        <TabsContent value="payment">
          <SettingCard icon={CreditCard} title="Payment Settings">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-sky-50 rounded-xl"><div><p className="text-sm font-medium">Cash on Delivery</p><p className="text-xs text-slate-500">Allow COD payment method</p></div><Switch checked={settings.cod_enabled !== false} onCheckedChange={v => u("cod_enabled", v)} /></div>
              <div><Label className="text-xs">Min Order for COD</Label><Input type="number" value={settings.min_order_cod || 100} onChange={e => u("min_order_cod", parseInt(e.target.value))} className="rounded-xl mt-1" /></div>
              <div className="p-3 bg-amber-50 rounded-xl text-xs text-amber-700">Payment gateway integration (Razorpay) is currently mocked. Configure API keys to enable real payments.</div>
            </div>
          </SettingCard>
        </TabsContent>

        <TabsContent value="delivery">
          <SettingCard icon={Truck} title="Delivery Settings">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Free Delivery Above (Rs.)</Label><Input type="number" value={settings.free_delivery_above || 499} onChange={e => u("free_delivery_above", parseInt(e.target.value))} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Standard Delivery (Rs.)</Label><Input type="number" value={settings.standard_delivery || 49} onChange={e => u("standard_delivery", parseInt(e.target.value))} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Express Delivery (Rs.)</Label><Input type="number" value={settings.express_delivery || 99} onChange={e => u("express_delivery", parseInt(e.target.value))} className="rounded-xl mt-1" /></div>
            </div>
          </SettingCard>
        </TabsContent>

        <TabsContent value="email">
          <SettingCard icon={Mail} title="Email Settings">
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 rounded-xl text-xs text-amber-700">Email notifications are currently mocked. Configure SMTP settings to enable real emails.</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">SMTP Host</Label><Input placeholder="smtp.gmail.com" className="rounded-xl mt-1" /></div>
                <div><Label className="text-xs">SMTP Port</Label><Input placeholder="587" className="rounded-xl mt-1" /></div>
                <div><Label className="text-xs">SMTP Username</Label><Input placeholder="email@domain.com" className="rounded-xl mt-1" /></div>
                <div><Label className="text-xs">SMTP Password</Label><Input type="password" placeholder="*****" className="rounded-xl mt-1" /></div>
              </div>
              <Button variant="outline" className="rounded-full text-sm">Send Test Email</Button>
            </div>
          </SettingCard>
        </TabsContent>

        <TabsContent value="security">
          <SettingCard icon={Shield} title="Security Settings">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-sky-50 rounded-xl"><div><p className="text-sm font-medium">Two-Factor Authentication</p><p className="text-xs text-slate-500">Require 2FA for admin login</p></div><Switch /></div>
              <div><Label className="text-xs">Session Timeout (minutes)</Label><Input type="number" defaultValue={60} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Max Login Attempts</Label><Input type="number" defaultValue={5} className="rounded-xl mt-1" /></div>
            </div>
          </SettingCard>
        </TabsContent>

        <TabsContent value="tax">
          <SettingCard icon={Receipt} title="Tax Settings">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">GST Rate (%)</Label><Input type="number" value={settings.gst_rate || 5} onChange={e => u("gst_rate", parseFloat(e.target.value))} className="rounded-xl mt-1" /></div>
            </div>
          </SettingCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
